import { shiftReportPayloadSchema, type ShiftReportPayload } from '@pms/shared';

import { enqueue } from '../sync/outbox.js';
import { dropDraft, type ReportDraft } from './draft.js';

/**
 * Отправка сменного отчёта.
 *
 * Отчёт уходит одним событием. Фотографии в нём только перечислены — сами файлы
 * идут отдельной очередью, поэтому смена считается сданной сразу после цифр.
 */

export function draftToPayload(draft: ReportDraft): ShiftReportPayload {
  const payload = {
    reportId: draft.id,
    siteId: draft.siteId,
    machineId: draft.machineId,
    date: draft.date,
    shiftNumber: draft.shiftNumber,
    participants: draft.participants,
    outputs: Object.entries(draft.outputs).map(([productSizeId, qtyPieces]) => ({
      productSizeId,
      qtyPieces,
      defectPieces: draft.defects[productSizeId] ?? 0,
    })),
    materials: Object.entries(draft.materials).map(([materialTypeId, entry]) => ({
      materialTypeId,
      direction: entry.direction,
      qtyG: entry.qtyG,
    })),
    batchesCount: draft.batchesCount,
    recipeId: draft.recipeId,
    incidents: draft.incidents.map((incident) => ({
      kind: incident.kind,
      durationMinutes: incident.durationMinutes,
      comment: incident.comment,
      traineeUserId: incident.traineeUserId,
    })),
    crewWork: draft.crewWork,
    comment: draft.comment,
    photos: draft.photos,
  };

  return shiftReportPayloadSchema.parse(payload);
}

/** Проверка перед отправкой — те же правила, что и на сервере, но сразу на экране. */
export function validateDraft(draft: ReportDraft): string[] {
  const problems: string[] = [];

  const result = shiftReportPayloadSchema.safeParse({
    reportId: draft.id,
    siteId: draft.siteId,
    machineId: draft.machineId,
    date: draft.date,
    shiftNumber: draft.shiftNumber,
    participants: draft.participants,
    outputs: Object.entries(draft.outputs).map(([productSizeId, qtyPieces]) => ({
      productSizeId,
      qtyPieces,
      defectPieces: draft.defects[productSizeId] ?? 0,
    })),
    materials: Object.entries(draft.materials).map(([materialTypeId, entry]) => ({
      materialTypeId,
      direction: entry.direction,
      qtyG: entry.qtyG,
    })),
    batchesCount: draft.batchesCount,
    recipeId: draft.recipeId,
    incidents: draft.incidents,
    crewWork: draft.crewWork,
    comment: draft.comment,
    photos: draft.photos,
  });

  if (!result.success) {
    for (const issue of result.error.issues) problems.push(issue.message);
  }

  return problems;
}

export async function submitDraft(draft: ReportDraft, userId: string): Promise<void> {
  const payload = draftToPayload(draft);

  await enqueue({
    type: 'SHIFT_REPORT_SUBMITTED',
    aggregateType: 'SHIFT_REPORT',
    aggregateId: draft.id,
    userId,
    payload: payload as unknown as Record<string, unknown>,
  });

  await dropDraft(draft.id);
}
